<script>
/* COMPONENTS */
import GaTripleView from '@components/v2/layout/GaTripleView.vue';
import AkAvatar from '@components/general/AkAvatar.vue';
import AkButtonActionValidation from '@components/v2/general/AkButtonActionValidation.vue';
import CommercialProspects from '@views/commercials/CommercialProspects.vue';

/* MIXINS */
import backMixin from '@mixins/backMixin';
import messageMixin from '@mixins/messageMixin';
import utilsMixin from '@mixins/utilsMixin';
import loaderMixin from '@mixins/loaderMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import civilitiesConst from '@mixins/const/civilitiesConst';
import countryConst from '@mixins/const/countryConst';
import ownerTypesConst from '@mixins/const/ownerTypesConst';

/* SERVICES */
import commercialService from '@services/commercialService';
import itemLabelService from '@services/itemLabelService';
import GaTabView from '@components/v2/layout/GaTabView.vue';
import CommercialCrm from '@views/commercials/CommercialCrm.vue';
import commercialTabMixin from '@mixins/tab/commercialTabMixin';
import validatorMixins from '@mixins/validatorMixins';
import DropdownMenu from 'v-dropdown-menu';
import useVuelidate from '@vuelidate/core';
import {helpers, maxLength, required} from '@vuelidate/validators';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkDialog from '@components/general/AkDialog.vue';

export default {
  components: {
    AkDialog,
    AkInputText,
    AkFormSubmitted,
    AkButtonActionV2,
    CommercialCrm,
    GaTabView,
    AkButtonActionValidation,
    AkAvatar,
    GaTripleView,
    CommercialProspects,
    DropdownMenu,
  },
  mixins: [
    permissionsMixin,
    backMixin,
    messageMixin,
    civilitiesConst,
    commercialTabMixin,
    utilsMixin,
    loaderMixin,
    countryConst,
    ownerTypesConst,
    validatorMixins,
  ],
  head() {
    return {
      title: this.title,
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      email: {
        required,
        maxLengthValue: maxLength(100),
        email: helpers.withMessage(
          "Le format de l'adresse e-mail est invalide. Veuillez vérifier et réessayer (ex. : nom@domaine.com)",
          this.emailValidator,
        ),
      },
    };
  },
  data() {
    return {
      commercial: {
        companyId: null,
        contact: {
          companyName: null,
          formeJuridiqueId: null,
          raisonSociale: null,
          capital: null,
          address: null,
          complement: null,
          postalCode: null,
          city: null,
          state: null,
          country: null,
          email: null,
          phone: null,
          villeRcs: null,
          siret: null,
          civility: null,
          lastName: null,
          firstName: null,
          function: null,
        },
      },
      listFormeJuridique: [],

      email: null,
      previousEmail: null,
      submitted: false,
    };
  },
  mounted() {
    this.initGoBack();
    this.refresh();
  },
  computed: {
    title() {
      return this.commercial.display ?? this.$t('commercial.details');
    },
  },
  methods: {
    refresh() {
      this.showTotalLoaderGuestadom();

      let p1 = commercialService.commercial(this.$route.params.commercialId);
      p1.then((data) => {
        this.commercial = data;
      });

      let p2 = itemLabelService.itemLabels({type: 'FORME_JURIDIQUE'});
      p2.then((data) => {
        this.listFormeJuridique = data;
      });

      Promise.all([p1, p2]).finally(() => this.hideLoader());
    },

    // ACTIONS

    generateAccess() {
      commercialService
        .access(this.$route.params.commercialId)
        .then((data) => {
          this.commercial = data;
          this.success(this.$t('commercial.accessSent'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          if (this.$refs.btnAccess) this.$refs.btnAccess.init();
        });
    },
    enableCommercial() {
      commercialService
        .enable(this.$route.params.commercialId)
        .then((data) => {
          this.commercial = data;
          this.commercial.userEnabled = true;
          this.success(this.$t('commercial.enabled'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
          this.$refs.btnEnable.init();
        });
    },
    disableCommercial() {
      commercialService
        .disable(this.$route.params.commercialId)
        .then((data) => {
          this.commercial = data;
          this.commercial.userEnabled = false;
          this.success(this.$t('commercial.disabled'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
          this.$refs.btnDisable.init();
        });
    },
    showEmailUpdateDialog() {
      this.previousEmail = this.commercial.contact.email;
      this.submitted = false;
      this.email = null;
      this.$refs.updateEmailDialog.show();
    },
    updateEmail() {
      this.submitted = true;
      this.v$.email.$touch();
      if (this.v$.email.$error) {
        this.$refs.updateEmailDialog.init();
        return;
      }

      commercialService
        .updateEmail(this.commercial.id, this.email)
        .then(() => {
          this.commercial.contact.email = this.email;

          this.addSuccess("L'adresse mail de l'agent commercial a été modifiée avec succès");
          this.$refs.updateEmailDialog.cancel();
        })
        .catch((e) => {
          this.$refs.updateEmailDialog.init();
          if (e.response.data.error === 'USER_MAIL_ALREADY_EXISTS')
            this.$refs.updateEmailDialog.error(
              "L'adresse mail saisie est déjà enregistrée dans l'application. Merci de vérifier le champ E-mail.",
            );
          else this.$refs.updateEmailDialog.error('Une erreur est survenu, merci de réessayer.');
        });
    },
  },
};
</script>

<template>
  <GaTripleView>
    <template #action>
      <AkButtonActionValidation
        v-if="!commercial.userEnabled"
        ref="btnEnable"
        :label="$t('enable')"
        :messagePopup="$t('confirm_enable', [this.commercial.display])"
        :titlePopup="$t('confirm_dialog')"
        success
        @click="enableCommercial" />
      <AkButtonActionValidation
        v-if="commercial.userEnabled"
        ref="btnDisable"
        :label="$t('disable')"
        :messagePopup="$t('confirm_disable', [this.commercial.display])"
        :titlePopup="$t('confirm_dialog')"
        danger
        @click="disableCommercial" />

      <AkButtonActionValidation
        v-if="!commercial.userId && commercial.userEnabled"
        ref="btnAccess"
        :label="$t('commercial.createAccess')"
        :messagePopup="$t('commercial.create_access_confirmation')"
        :titlePopup="$t('confirm_dialog')"
        secondary
        @click="generateAccess" />
      <AkButtonActionValidation
        v-if="commercial.userId && commercial.userEnabled"
        ref="btnAccess"
        :label="$t('commercial.resendAccess')"
        :messagePopup="$t('commercial.resend_access_confirmation')"
        :titlePopup="$t('confirm_dialog')"
        secondary
        @click="generateAccess" />

      <dropdown-menu :overlay="false" direction="right" class="accommodation" v-if="commercial.id">
        <template #trigger>
          <span class="btn-primary xs">Modifier</span>
        </template>
        <template #body>
          <ul>
            <li class="py-2">
              <router-link
                class="text-grey-400 font-medium font-inter text-base md:hover:text-grey-900"
                :to="{name: 'commercialUpdate', params: {commercialId: commercial.id}}"
                >{{ $t('update') }}
              </router-link>
            </li>
            <li class="py-2">
              <AkButtonActionV2
                label="Modifier l'e-mail"
                class="text-grey-400 font-medium font-inter !bg-transparent !p-0 ext-base md:hover:text-grey-900"
                @click="showEmailUpdateDialog" />
            </li>
          </ul>
        </template>
      </dropdown-menu>
    </template>
    <template #left>
      <div class="flex flex-col gap-4 md:gap-2">
        <div class="w-[160px] self-center">
          <AkAvatar
            v-if="commercial.userId"
            ref="avatar"
            :first-name="commercial.contact.firstName"
            :last-name="commercial.contact.lastName"
            :user-id="commercial.userId" />
        </div>

        <div class="flex items-center gap-2 mt-3">
          <i
            class="ga-icon ga-chevron_down text-grey-900 text-2xl md:hover:cursor-pointer"
            @click="toggleElement(this.$refs, 'contact', $event)" />
          <span class="font-inter text-grey-900 font-semibold text-lg">Contact</span>
        </div>
        <div ref="contact">
          <div class="grid grid-cols-2 mt-4 gap-4 md:gap-2">
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.formeJuridique') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                listFormeJuridique.find((f) => f.id === commercial.contact.formeJuridiqueId)?.label
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.raisonSociale') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.raisonSociale
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.companyName') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.companyName
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">Capital (€)</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.capital
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.villeRcs') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.villeRcs
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.siret') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.siret
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.civility') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                displayForCivilityConst(commercial.contact.civility)
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.functionManager') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.function
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.lastNameManager') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.lastName
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.firstNameManager') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.firstName
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.email') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.email
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.phone') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                commercial.contact.phone
              }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 mt-3">
          <i
            class="ga-icon ga-chevron_down text-grey-900 text-2xl md:hover:cursor-pointer"
            @click="toggleElement(this.$refs, 'address', $event)" />
          <span class="font-inter text-grey-900 font-semibold text-lg">Adresse</span>
        </div>
        <div ref="address">
          <div :class="commercial.contact.address ? 'grid-cols-2' : 'grid-cols-1'" class="grid mt-4 gap-4 md:gap-2">
            <template v-if="commercial.contact.address">
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{
                  $t('contact.address1Manager')
                }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  commercial.contact.address
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('address.complement') }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  commercial.contact.complement
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('address.postalCode') }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  commercial.contact.postalCode
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('address.city') }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  commercial.contact.city
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('address.state') }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  commercial.contact.state
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('address.country') }}</span>
                <span class="font-inter font-medium text-grey-400 text-base break-words">{{
                  this.displayForCountryConst(commercial.contact.country)
                }}</span>
              </div>
            </template>
            <div v-else class="flex flex-col gap-1">
              <span class="font-inter font-semibold text-grey-900 text-base">{{ $t('contact.address1Manager') }}</span>
              <span class="font-inter font-medium text-grey-400 text-base break-words">Adresse inconnue</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <GaTabView :tabs="tabs">
        <template #crm>
          <CommercialCrm />
        </template>
        <template #prospect>
          <CommercialProspects />
        </template>
      </GaTabView>
    </template>
    <template #right></template>
  </GaTripleView>

  <AkDialog title="Modifier l'e-mail" ref="updateEmailDialog" :validate-label="$t('modify')" @validate="updateEmail">
    <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-2">
      <AkInputText v-model="this.previousEmail" label="Ancienne adresse mail" disabled />
      <AkInputText v-model="this.email" :validator="v$.email" label="Nouvelle adresse mail" autofocus />
    </AkFormSubmitted>
  </AkDialog>
</template>
